import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  User,
  FileText,
  MapPin,
  Download,
  Eye,
  Clock,
  ArrowRight,
  Activity
} from 'lucide-react';

interface Document {
  id: string;
  name: string;
  status: 'Approved' | 'Pending' | 'Rejected';
  uploadedDate: string;
  url?: string;
}

interface ActivityLog {
  id: string;
  action: string;
  description: string;
  timestamp: string;
  user: string;
  statusCard?: string;
}

interface ApplicantDetail {
  id: string;
  visaId: string;
  tourRefNo: string;
  partnerName: string;
  officeName: string;
  
  // Personal Info
  applicantName: string;
  applicantITS: string;
  passportNo: string;
  nationality: string;
  dateOfBirth: string;
  gender: string;
  
  // Visa Info
  visaRequired: boolean;
  visaFee?: number;
  visaType?: string;
  venue?: string;
  
  // Status
  status: 'Approved';
  completedDate: string;
  approvedBy: string;
  
  // Documents
  documents: Document[];
  
  // Activity Logs
  activityLogs: ActivityLog[];
}

interface CompletedApplicationViewPageProps {
  onNavigate?: (page: string) => void;
  applicantId?: string;
}

// Mock applicant data
const mockApplicantDetail: ApplicantDetail = {
  id: 'app1',
  visaId: 'VISA-2025001',
  tourRefNo: 'NKERP/TOUR/2025/4580',
  partnerName: 'Al-Mustafa Tours',
  officeName: 'Mumbai Main',
  
  applicantName: 'Mulla Mohammed Ahmed Khan',
  applicantITS: '12345678',
  passportNo: 'J1234567',
  nationality: 'India',
  dateOfBirth: '15-03-1985',
  gender: 'Male',
  
  visaRequired: true,
  visaFee: 120.00,
  visaType: 'Tourist Visa',
  venue: 'Iraq',
  
  status: 'Approved',
  completedDate: '22-01-2025 14:30',
  approvedBy: 'Admin User',
  
  documents: [
    { id: 'doc1', name: 'Passport Copy', status: 'Approved', uploadedDate: '20-01-2025' },
    { id: 'doc2', name: 'Photograph', status: 'Approved', uploadedDate: '20-01-2025' },
    { id: 'doc3', name: 'ITS Card', status: 'Approved', uploadedDate: '20-01-2025' },
    { id: 'doc4', name: 'Hotel Booking', status: 'Approved', uploadedDate: '20-01-2025' }
  ],
  
  activityLogs: [
    {
      id: 'log1',
      action: 'Application Created',
      description: 'Visa application created and submitted by Tour Operator',
      timestamp: '18-01-2025 10:15 AM',
      user: 'Al-Mustafa Tours',
      statusCard: 'New Application'
    },
    {
      id: 'log2',
      action: 'Moved to Visa Document Required',
      description: 'Application moved to Visa Document Required for fee collection',
      timestamp: '19-01-2025 09:30 AM',
      user: 'System',
      statusCard: 'Visa Document Required'
    },
    {
      id: 'log3',
      action: 'Documents Uploaded',
      description: 'All required documents uploaded by Tour Operator',
      timestamp: '20-01-2025 11:45 AM',
      user: 'Al-Mustafa Tours',
      statusCard: 'Visa Document Required'
    },
    {
      id: 'log4',
      action: 'Fees Collected',
      description: 'Visa fee of $120 collected and verified',
      timestamp: '20-01-2025 02:20 PM',
      user: 'Admin User',
      statusCard: 'Visa Document Required'
    },
    {
      id: 'log5',
      action: 'Moved to To Be Sent for Processing',
      description: 'Application approved and ready to be sent for processing',
      timestamp: '20-01-2025 03:15 PM',
      user: 'Admin User',
      statusCard: 'To Be Sent for Processing'
    },
    {
      id: 'log6',
      action: 'Documents Reviewed',
      description: 'All documents reviewed and approved by admin',
      timestamp: '21-01-2025 10:00 AM',
      user: 'Admin User',
      statusCard: 'To Be Sent for Processing'
    },
    {
      id: 'log7',
      action: 'Sent for Processing',
      description: 'Application sent to Muwafiqa for visa processing',
      timestamp: '21-01-2025 11:30 AM',
      user: 'Admin User',
      statusCard: 'To Be Sent for Processing'
    },
    {
      id: 'log8',
      action: 'Moved to Update Applicant Status',
      description: 'Application received from Muwafiqa and pending status update',
      timestamp: '22-01-2025 09:00 AM',
      user: 'System',
      statusCard: 'Update Applicant Status'
    },
    {
      id: 'log9',
      action: 'Status Updated - Approved',
      description: 'Visa application approved by Muwafiqa',
      timestamp: '22-01-2025 02:15 PM',
      user: 'Admin User',
      statusCard: 'Update Applicant Status'
    },
    {
      id: 'log10',
      action: 'Moved to Completed',
      description: 'Application successfully completed and moved to completed status',
      timestamp: '22-01-2025 02:30 PM',
      user: 'Admin User',
      statusCard: 'Completed'
    }
  ]
};

export const CompletedApplicationViewPage = ({ onNavigate, applicantId }: CompletedApplicationViewPageProps) => {
  const [applicant] = useState(mockApplicantDetail);
  const [showActivityLogs, setShowActivityLogs] = useState(false);

  const getDocumentBadge = (status: string) => {
    switch (status) {
      case 'Approved':
        return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'Rejected':
        return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getActivityIcon = (action: string) => {
    if (action.includes('Approved') || action.includes('Completed')) {
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    } else if (action.includes('Rejected')) {
      return <XCircle className="w-4 h-4 text-red-600" />;
    } else if (action.includes('Moved')) {
      return <ArrowRight className="w-4 h-4 text-blue-600" />;
    } else {
      return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 sm:p-6 lg:p-8">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => onNavigate && onNavigate('visa-muwafiqa/completed')}
        className="mb-4 hover:bg-sky-50"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Completed List
      </Button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-gray-900">Application Details</h1>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {applicant.status}
                </Badge>
              </div>
              <p className="text-sm text-gray-600">{applicant.visaId}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-md" style={{ gap: "0px" }}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Full Name</p>
                  <p className="text-sm text-gray-900">{applicant.applicantName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">ITS Number</p>
                  <p className="text-sm font-semibold text-gray-900">{applicant.applicantITS}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Passport Number</p>
                  <p className="text-sm text-gray-900">{applicant.passportNo}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Nationality</p>
                  <p className="text-sm text-gray-900">{applicant.nationality}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Date of Birth</p>
                  <p className="text-sm text-gray-900">{applicant.dateOfBirth}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Gender</p>
                  <p className="text-sm text-gray-900">{applicant.gender}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tour Information */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-md" style={{ gap: "0px" }}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                Tour Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Tour Reference No.</p>
                  <p className="text-sm font-semibold" style={{ color: '#5B9BD5' }}>{applicant.tourRefNo}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Partner Name</p>
                  <p className="text-sm text-gray-900">{applicant.partnerName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Office Name</p>
                  <p className="text-sm text-gray-900">{applicant.officeName}</p>
                </div>
                {applicant.venue && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Venue</p>
                    <p className="text-sm text-gray-900">{applicant.venue}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Visa Information */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-md" style={{ gap: "0px" }}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                Visa Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Visa Required</p>
                  {applicant.visaRequired ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Yes
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600 border-gray-300">
                      No
                    </Badge>
                  )}
                </div>
                {applicant.visaRequired && (
                  <>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Visa Fee</p>
                      <p className="text-sm font-semibold text-gray-900">${applicant.visaFee?.toFixed(2)}</p>
                    </div>
                    {applicant.visaType && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Visa Type</p>
                        <p className="text-sm text-gray-900">{applicant.visaType}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-md" style={{ gap: "0px" }}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                Documents ({applicant.documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {applicant.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">Uploaded: {doc.uploadedDate}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getDocumentBadge(doc.status)}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        style={{ color: '#5B9BD5' }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200 shadow-md" style={{ gap: "0px" }}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Status Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Current Status</p>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Completed Date</p>
                <p className="text-sm text-gray-900">{applicant.completedDate}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Approved By</p>
                <p className="text-sm text-gray-900">{applicant.approvedBy}</p>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity Summary */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-md" style={{ gap: "0px" }}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {applicant.activityLogs.slice(0, 3).map((log) => (
                  <div key={log.id} className="flex gap-2">
                    <div className="mt-1">{getActivityIcon(log.action)}</div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{log.action}</p>
                      <p className="text-xs text-gray-500">{log.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                variant="link"
                onClick={() => setShowActivityLogs(true)}
                className="mt-3 p-0 h-auto text-xs"
                style={{ color: '#5B9BD5' }}
              >
                View All Activity ({applicant.activityLogs.length})
              </Button>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-md" style={{ gap: "0px" }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}
              >
                <Download className="w-4 h-4" />
                Download Application
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}
              >
                <FileText className="w-4 h-4" />
                View All Documents
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Activity Logs Dialog */}
      <Dialog open={showActivityLogs} onOpenChange={setShowActivityLogs}>
        <DialogContent className="sm:max-w-[900px] max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" style={{ color: '#5B9BD5' }} />
              Activity Logs - {applicant.visaId}
            </DialogTitle>
            <DialogDescription>
              Complete history of status changes, approvals, and movements for this application
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[500px] overflow-y-auto">
            {applicant.activityLogs.map((log, index) => (
              <div
                key={log.id}
                className="relative pl-8 pb-4 border-l-2 border-gray-200 last:border-l-0 last:pb-0"
              >
                <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center">
                  {index === 0 && (
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                  )}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getActivityIcon(log.action)}
                      <p className="text-sm font-semibold text-gray-900">{log.action}</p>
                    </div>
                    {log.statusCard && (
                      <Badge variant="outline" className="text-xs" style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}>
                        {log.statusCard}
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-600 mb-2">{log.description}</p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {log.user}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {log.timestamp}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowActivityLogs(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};